import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Shield, Calculator, CheckCircle, Save, Pencil } from "lucide-react";
import {
  getOffboarding,
  updateOffboarding,
  submitOffboarding,
  startClearance,
  updateClearance,
  saveExitInterview,
  calculateSettlement,
  autoCalculateSettlement,
  completeOffboarding,
  offboardingApprovalAction,
  formatLifecycleError,
  getClearanceProgress,
} from "./lifecycleApi";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";
import DatePickerField from "../../components/common/DatePickerField";

const errorText = (err, fallback = "Request failed") =>
  typeof err === "string" ? err : formatLifecycleError({ response: { data: { detail: err } } }, fallback);

const TABS = ["Details", "Clearance", "Exit Interview", "F&F Settlement", "Approvals"];

export default function LcOffboardingDetail() {
  const { id } = useParams();
  const [off, setOff] = useState(null);
  const [tab, setTab] = useState("Details");
  const [form, setForm] = useState({});
  const [interview, setInterview] = useState({});
  const [settlement, setSettlement] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clearanceNames, setClearanceNames] = useState({});
  const [savingClearanceId, setSavingClearanceId] = useState(null);
  const [editClearanceNames, setEditClearanceNames] = useState(false);

  const isExitLocked = off?.status === "Exited" || off?.status === "Archived";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOffboarding(id);
      setOff(data);
      setForm({
        separation_type: data.separation_type || "",
        last_working_day: data.last_working_day || "",
        notice_period_start: data.notice_period_start || "",
        notice_period_end: data.notice_period_end || "",
        reason_for_exit: data.reason_for_exit || "",
        manager_remarks: data.manager_remarks || "",
        hr_remarks: data.hr_remarks || "",
      });
      if (data.exit_interview) setInterview(data.exit_interview);
      if (data.settlement) setSettlement(data.settlement);
      const names = {};
      (data.clearances || []).forEach((c) => {
        names[c.clearance_id] = c.item_name || "";
      });
      setClearanceNames(names);
    } catch (e) {
      setError(formatLifecycleError(e, "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveClearanceName = async (clearanceId) => {
    const name = (clearanceNames[clearanceId] || "").trim();
    if (!name) {
      setError("Clearance item name cannot be empty");
      return;
    }
    setSavingClearanceId(clearanceId);
    setError("");
    try {
      await updateClearance(id, clearanceId, { item_name: name });
      await load();
    } catch (e) {
      setError(formatLifecycleError(e, "Failed to save item name"));
    } finally {
      setSavingClearanceId(null);
    }
  };

  const action = async (fn) => {
    try {
      setError("");
      await fn();
      await load();
    } catch (e) {
      setError(formatLifecycleError(e, "Action failed"));
    }
  };

  if (loading) return <BrixigoPageLoader compact message="Loading…" />;
  if (!off) return <p className="text-red-600">{errorText(error, "Not found")}</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-xl font-semibold">{off.employee_name || "Offboarding"}</h2>
            <p className="text-sm text-slate-500">
              {off.employee_code ? `${off.employee_code} · ` : ""}{off.status} · {off.separation_type}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {off.status === "Draft" && (
            <button type="button" onClick={() => action(() => submitOffboarding(id))} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-amber-500 text-white text-sm">
              <Send className="w-4 h-4" /> Submit
            </button>
          )}
          {["Submitted", "Notice Period"].includes(off.status) && (
            <button type="button" onClick={() => action(() => startClearance(id))} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-500 text-white text-sm">
              <Shield className="w-4 h-4" /> Start Clearance
            </button>
          )}
          {off.status !== "Exited" && off.status !== "Archived" && (
            <button type="button" onClick={() => action(() => completeOffboarding(id))} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-red-600 text-white text-sm">
              <CheckCircle className="w-4 h-4" /> Complete Exit
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {errorText(error)}
        </div>
      )}

      <div className="flex gap-2 border-b pb-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tab === t ? "bg-violet-600 text-white" : ""}`}>{t}</button>
        ))}
      </div>

      {tab === "Details" && (
        <div className="rounded-xl border p-4 space-y-3" style={{ background: "var(--bg-surface, #fff)" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm block">
              Separation Type
              <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.separation_type} onChange={(e) => setForm({ ...form, separation_type: e.target.value })} />
            </label>
            <label className="text-sm block">
              Last Working Day
              <div className="mt-1">
                <DatePickerField
                  hideLabel
                  value={form.last_working_day || ""}
                  onChange={(date) => setForm({ ...form, last_working_day: date })}
                  inputClassName="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </label>
            <label className="text-sm block col-span-2">
              Exit Reason
              <textarea className="mt-1 w-full border rounded-lg px-3 py-2" rows={2} value={form.reason_for_exit} onChange={(e) => setForm({ ...form, reason_for_exit: e.target.value })} />
            </label>
            <label className="text-sm block col-span-2">
              Manager Remarks
              <textarea className="mt-1 w-full border rounded-lg px-3 py-2" rows={2} value={form.manager_remarks} onChange={(e) => setForm({ ...form, manager_remarks: e.target.value })} />
            </label>
            <label className="text-sm block col-span-2">
              HR Remarks
              <textarea className="mt-1 w-full border rounded-lg px-3 py-2" rows={2} value={form.hr_remarks} onChange={(e) => setForm({ ...form, hr_remarks: e.target.value })} />
            </label>
          </div>
          <button type="button" onClick={() => action(() => updateOffboarding(id, form))} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm">Save</button>
        </div>
      )}

      {tab === "Clearance" && (() => {
        const { done, total } = getClearanceProgress(off.clearances);
        return (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">
                Clearance progress: {done}/{total}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Mark each item cleared when complete. Edit names only if you need to customize labels.
              </p>
            </div>
            {!isExitLocked && (
              <button
                type="button"
                onClick={() => setEditClearanceNames((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                  editClearanceNames
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                <Pencil className="w-3.5 h-3.5" />
                {editClearanceNames ? "Done editing names" : "Edit item names"}
              </button>
            )}
          </div>
          {["HR", "IT", "Admin", "Asset"].map((dept) => (
            <div key={dept} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                <h4 className="font-semibold text-sm text-slate-800">{dept} Clearance</h4>
              </div>
              <div className="divide-y divide-slate-100">
                {(off.clearances || []).filter((c) => c.department === dept).map((c) => {
                  const draftName = clearanceNames[c.clearance_id] ?? c.item_name ?? "";
                  const nameDirty = draftName.trim() !== (c.item_name || "").trim();

                  return (
                    <div key={c.clearance_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                      <div className="flex flex-1 min-w-[200px] items-center gap-2">
                        {editClearanceNames && !isExitLocked ? (
                          <>
                            <input
                              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/25 focus:border-violet-400"
                              value={draftName}
                              placeholder="Clearance item name"
                              onChange={(e) =>
                                setClearanceNames((prev) => ({ ...prev, [c.clearance_id]: e.target.value }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveClearanceName(c.clearance_id);
                              }}
                            />
                            {nameDirty && (
                              <button
                                type="button"
                                title="Save name"
                                disabled={savingClearanceId === c.clearance_id}
                                onClick={() => saveClearanceName(c.clearance_id)}
                                className="p-2 rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-60"
                              >
                                <Save className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="font-medium text-slate-800">{c.item_name}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                            c.status === "Cleared"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {c.status}
                        </span>
                        {!isExitLocked && c.status !== "Cleared" && dept !== "Asset" && (
                          <button
                            type="button"
                            className="text-xs font-medium text-emerald-700 hover:text-emerald-900 px-2.5 py-1 rounded-lg bg-emerald-50"
                            onClick={() => action(() => updateClearance(id, c.clearance_id, { status: "Cleared" }))}
                          >
                            Clear
                          </button>
                        )}
                        {!isExitLocked && dept === "Asset" && c.asset_status !== "Returned" && (
                          <button
                            type="button"
                            className="text-xs font-medium text-emerald-700 hover:text-emerald-900 px-2.5 py-1 rounded-lg bg-emerald-50"
                            onClick={() =>
                              action(() =>
                                updateClearance(id, c.clearance_id, {
                                  asset_status: "Returned",
                                  status: "Cleared",
                                })
                              )
                            }
                          >
                            Returned
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      {tab === "Exit Interview" && (
        <div className="rounded-xl border p-4 space-y-3" style={{ background: "var(--bg-surface, #fff)" }}>
          {[
            ["reason_for_leaving", "Reason for leaving"],
            ["manager_feedback", "Manager feedback"],
            ["organization_feedback", "Organization feedback"],
            ["improvement_suggestions", "Improvement suggestions"],
          ].map(([key, label]) => (
            <label key={key} className="block text-sm">
              {label}
              <textarea className="mt-1 w-full border rounded-lg px-3 py-2" rows={2} value={interview[key] || ""} onChange={(e) => setInterview({ ...interview, [key]: e.target.value })} />
            </label>
          ))}
          <label className="block text-sm">
            Overall Rating (1-5)
            <input type="number" min={1} max={5} className="mt-1 w-24 border rounded-lg px-3 py-2" value={interview.overall_rating || ""} onChange={(e) => setInterview({ ...interview, overall_rating: Number(e.target.value) })} />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!interview.rehire_eligible} onChange={(e) => setInterview({ ...interview, rehire_eligible: e.target.checked })} />
            Rehire eligible
          </label>
          <button type="button" onClick={() => action(() => saveExitInterview(id, interview))} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm">Save Interview</button>
        </div>
      )}

      {tab === "F&F Settlement" && (
        <div className="rounded-xl border p-4 space-y-3" style={{ background: "var(--bg-surface, #fff)" }}>
          {[
            ["unpaid_salary", "Unpaid Salary"],
            ["notice_pay", "Notice Pay"],
            ["leave_encashment", "Leave Encashment"],
            ["gratuity", "Gratuity"],
            ["bonus", "Bonus"],
            ["incentives", "Incentives"],
            ["recoveries", "Recoveries"],
            ["loan_recovery", "Loan Recovery"],
            ["notice_pay_recovery", "Notice Pay Recovery"],
            ["asset_damage_charges", "Asset Recovery / Damage"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center justify-between text-sm">
              <span>{label}</span>
              <input type="number" className="w-32 border rounded-lg px-2 py-1 text-right" value={settlement[key] || 0} onChange={(e) => setSettlement({ ...settlement, [key]: e.target.value })} />
            </label>
          ))}
          {off.settlement?.net_payable != null && (
            <p className="font-semibold text-lg pt-2 border-t">Net Payable: ₹{off.settlement.net_payable}</p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => action(() => autoCalculateSettlement(id))} className="inline-flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm">
              <Calculator className="w-4 h-4" /> Auto from Payroll
            </button>
            <button type="button" onClick={() => action(() => calculateSettlement(id, settlement))} className="inline-flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">
              <Calculator className="w-4 h-4" /> Save F&amp;F
            </button>
          </div>
        </div>
      )}

      {tab === "Approvals" && (() => {
        const approvals = [...(off.approvals || [])].sort((a, b) => a.sequence - b.sequence);
        const currentStep = approvals.find((a) => a.status === "Pending");
        const allDone = approvals.length > 0 && approvals.every((a) => a.status === "Completed");

        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
              <p className="font-semibold">How to complete offboarding approvals</p>
              <ol className="mt-2 list-decimal list-inside space-y-1 text-violet-800">
                <li>Fill <strong>Details</strong> and click <strong>Save</strong></li>
                <li>Click <strong>Submit</strong> (top right) to start the exit</li>
                <li>Complete <strong>Clearance</strong>, <strong>Exit Interview</strong>, and <strong>F&amp;F Settlement</strong></li>
                <li>Approve each step below <strong>in order</strong> (Admin can act for each role)</li>
                <li>When clearance is done, click <strong>Complete Exit</strong></li>
              </ol>
            </div>

            {allDone && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 font-medium">
                All approval steps completed.
              </div>
            )}

            {currentStep && !allDone && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Current step: <strong>{currentStep.approval_role}</strong> (Step {currentStep.sequence})
              </div>
            )}

            <div className="space-y-2">
              {approvals.map((ap) => {
                const isCurrent = currentStep?.approval_id === ap.approval_id;
                const statusClass =
                  ap.status === "Completed"
                    ? "bg-emerald-100 text-emerald-800"
                    : ap.status === "Rejected"
                      ? "bg-red-100 text-red-800"
                      : isCurrent
                        ? "bg-amber-100 text-amber-800"
                        : "bg-slate-100 text-slate-600";

                return (
                  <div
                    key={ap.approval_id}
                    className={`rounded-xl border p-4 flex flex-wrap justify-between items-center gap-3 ${
                      isCurrent ? "border-violet-300 ring-1 ring-violet-200" : ""
                    }`}
                    style={{ background: "var(--bg-surface, #fff)" }}
                  >
                    <div>
                      <div className="font-medium text-sm text-slate-800">{ap.approval_role}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Step {ap.sequence}
                        {ap.acted_at && ` · ${new Date(ap.acted_at).toLocaleString()}`}
                      </div>
                      {ap.comments && (
                        <p className="text-xs text-slate-600 mt-1">Note: {ap.comments}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusClass}`}>
                        {ap.status}
                        {ap.action ? ` · ${ap.action}` : ""}
                      </span>
                      {ap.status === "Pending" && isCurrent && off.status !== "Exited" && (
                        <>
                          <button
                            type="button"
                            className="px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition"
                            onClick={() =>
                              action(() => offboardingApprovalAction(id, ap.approval_id, { action: "Approve" }))
                            }
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                            onClick={() =>
                              action(() => offboardingApprovalAction(id, ap.approval_id, { action: "Reject" }))
                            }
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
